<template>
  <div class="gov-notify-service-configuration">
    <section>
      <h3>Configure Your Services</h3>
      <div class="subtitle">Copy the information from Gov.UK Notify</div>
      <n-data-table :columns="serviceDataTableColumns" :items="serviceData" :loading="loading">
        <template
          v-for="slot in ['name', 'phoneNumber', 'notifyApiKey', 'templateName', 'bearerToken', 'actions']"
          #[slot]="slotProps"
        >
          <GovNotifyServiceConfigurationRow
            v-model="slotProps.row"
            :workspace-id="currentWorkspace.id"
            :column="slot"
            @update:modelValue="onRowUpdate"
            @delete="handleDeleteClick"
            @copy="onCopy"
          />
        </template>
      </n-data-table>
      <n-button variant="ghost" @click="addEmptyRow" icon="add" id="add-service">Add Service</n-button>
    </section>
    <section>
      <h3>Configure Gov.UK Notify Callbacks</h3>
      <div class="subtitle">Complete your setup in Gov.UK Notify</div>
      <div class="field">
        <n-readonly-input
          :value="deliveryCallbackUrl"
          label="Delivery Receipts Callback URL"
          @copy="onCopy"
        ></n-readonly-input>
      </div>

      <div class="field">
        <n-readonly-input
          :value="messageCallbackUrl"
          label="Received Messages Callback URL: "
          @copy="onCopy"
        ></n-readonly-input>
      </div>
    </section>
  </div>
</template>

<script lang="ts" setup>
import Toast from '@/shared/services/toast';
import { GovNotifyServiceViewModel } from '../models/govNotifyServiceViewModel';
import { govNotifyApi } from '../services/govNotifyApi';
import { useStore } from 'vuex';
import { computed, PropType, ref, watch } from 'vue';
import GovNotifyServiceConfigurationRow from '@/features/apps/govuknotifysms/components/GovNotifyServiceConfigurationRow.vue';

const serviceDataTableColumns = [
  { name: 'name', display: 'Name' },
  { name: 'phoneNumber', display: 'Phone Number' },
  { name: 'notifyApiKey', display: 'API Key' },
  { name: 'templateName', display: 'Template' },
  { name: 'bearerToken', display: 'Bearer Token' },
];
const messageCallbackUrl = govNotifyApi.getMessageWebhookUrl();
const deliveryCallbackUrl = govNotifyApi.getDeliveryWebhookUrl();

const store = useStore();
const currentWorkspace = computed(() => store.getters.currentWorkspace);
const props = defineProps({
  value: {
    type: Array as PropType<GovNotifyServiceViewModel[]>,
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  (e: 'input', value: Array<Partial<GovNotifyServiceViewModel>>): void;
}>();

const loadingToken = ref(false);
const serviceData = ref<Array<Partial<GovNotifyServiceViewModel>>>([]);
const serviceToDelete = ref<GovNotifyServiceViewModel | null>();
const hasInitialised = computed(() => props.value && props.value.length > 0);

watch(
  () => props.value,
  () => {
    serviceData.value = props.value || [];
    try {
      if (props.value.length === 0 && !props.loading && !hasInitialised.value) {
        addEmptyRow();
      }
    } catch (e: any) {
      console.error('Error setting initial row', e);
    }
  },
  { immediate: true },
);

async function addEmptyRow() {
  loadingToken.value = true;
  try {
    const newService = {
      id: '',
      bearerToken: 'Loading...',
      notifyApiKey: '',
      name: '',
      phoneNumber: '',
      isDraft: true,
    };
    serviceData.value.push(newService);

    const { bearerToken } = await govNotifyApi.getBearerToken(currentWorkspace.value.id);

    newService.bearerToken = bearerToken;
    newService.id = getServiceIdFromBearerToken(bearerToken);
    if (!newService.id) {
      Toast.showError('No service ID in the bearer token');
    }

    emit('input', serviceData.value);
  } catch (err: any) {
    Toast.showError('Failed to add service for configuration: ' + err.message);
  }
  loadingToken.value = false;
}

function onRowUpdate(updatedRow: Partial<GovNotifyServiceViewModel>) {
  const serviceId = updatedRow.id;
  if (!updatedRow.id) {
    return;
  }
  const index = serviceData.value.findIndex((row: Partial<GovNotifyServiceViewModel>) => row.id === serviceId);
  if (index !== -1) {
    serviceData.value[index] = updatedRow;
    serviceData.value = [...serviceData.value];
    emit('input', serviceData.value);
  }
}

function onCopy() {
  Toast.showInfo('Copied to clipboard');
}

function handleDeleteClick(row: GovNotifyServiceViewModel) {
  serviceToDelete.value = row;
  deleteService();
}

function deleteService() {
  if (serviceToDelete.value) {
    serviceData.value = serviceData.value.filter((service) => service !== serviceToDelete.value);
    emit('input', serviceData.value);
  }
}

function getServiceIdFromBearerToken(bearerToken: string) {
  const claims = JSON.parse(atob(bearerToken.split('.')[1]));
  return claims.serviceId;
}
</script>

<style lang="scss" scoped>
section {
  margin-bottom: 1rem;
}
h3 {
  font-weight: 500;
  font-size: 1.2em;
}

.subtitle {
  opacity: 0.7;
  line-height: 1.35;
  font-size: 1rem;
  color: #212529;
}

.no-services {
  text-align: center;
  padding: 1rem;
  opacity: 0.7;
}

#add-service {
  margin: 0.5rem 0;
}

.actions-column {
  .btn {
    margin: 0.3rem 0;
  }
}
</style>
